import '@/styles/globals.css'

import type { FC, ReactNode } from 'react'
import type { AppProps } from 'next/app'
import type { AdminSession } from '@/types/marketplace'
import type { AdminBadges } from '@/components/admin/admin-layout'
import type { ConsoleIdentity } from '@/lib/community/console-guard'

import Head from 'next/head'

import { Toaster } from '@/components/ui/sonner'
import { AdminLayout } from '@/components/admin/admin-layout'
import { ConsoleLayout } from '@/components/community/console-layout'
import { consolePageKey } from '@/lib/community/console-key'
import { fontRootVariables } from '@/lib/fonts'

/**
 * Which shell a page belongs in is decided by what its own server loader
 * returned, never by its URL alone.
 *
 * A platform console page returns `session` and gets the platform sidebar. A
 * Community Console page returns `console` and gets the community sidebar. The
 * two are mutually exclusive by construction: `requireConsolePage` never returns
 * a session, so a community operator cannot be shown platform navigation even
 * though both products live under `/admin`. The sign-in, connect and
 * access-denied screens return neither and render on their own, exactly as
 * before.
 */
type AdminPageProps = {
    session?: AdminSession
    badges?: AdminBadges
    console?: ConsoleIdentity
}

const App: FC<AppProps> = ({ Component, pageProps, router }): ReactNode => {
    const { session, badges, console: consoleIdentity } = pageProps as AdminPageProps

    /*
     * Keyed on the MARKETPLACE, so switching tenant remounts the page and every
     * piece of client state it was holding for the previous one. See
     * `@/lib/community/console-key` for what leaked without it, including a
     * settings form that would have written one marketplace's values into
     * another and a Telegram connect code shown under the wrong community.
     *
     * That module is a LEAF with no imports of its own, deliberately: this file
     * is the root of the client bundle, so every page on the site pays for
     * whatever it pulls in.
     *
     * Undefined for every non-console page, which leaves React's normal
     * reconciliation exactly as it was.
     */
    const page = <Component {...pageProps} key={consolePageKey(consoleIdentity?.communityId)} />

    return (
        <div className='h-full font-sans'>
            <Head>
                <meta name='viewport' content='width=device-width, initial-scale=1' />
                <style dangerouslySetInnerHTML={{ __html: fontRootVariables }} />
            </Head>

            {/*
                Mounted here rather than inside each page, so the navigation is a
                single persistent instance: moving between console pages swaps
                only the content column and never remounts the sidebar.
            */}
            {router.pathname.startsWith('/admin') && consoleIdentity
                ? (
                    <ConsoleLayout identity={consoleIdentity}>
                        {page}
                    </ConsoleLayout>
                )
                : router.pathname.startsWith('/admin') && session
                    ? (
                        <AdminLayout session={session} badges={badges}>
                            {page}
                        </AdminLayout>
                    )
                    : page}

            <Toaster position='top-center' />
        </div>
    )
}

export default App
